/// <amd-module name="Repos/Views/CommitHub/Components/CommitHub" />
import * as React from "react";
import { BranchStats } from "Repos/Changes/Contracts";
import { getGitNavigationService, IGitNavigationService } from "Repos/ChangesCommon/ChangesNavigationService";
import { IChange } from "Repos/ChangesCommon/Contracts";
import { themeIndependentStyle } from "Repos/Common/Constants";
import { GitStatus } from "Repos/Common/Contracts/Git";
import { getReposRoutingService, IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { getRefNameFromBranchName, getShortCommitId } from "Repos/Common/Util/Ref";
import { getGitRepository } from "Repos/Common/VCViewModelDataProviderService";
import { ReposMessages } from "Repos/Messages/Components/ReposMessages";
import { PipelineStatus } from "Repos/PipelineStatus/Components/PipelineStatus";
import { ICommitViewService } from "Repos/Views/CommitHub/CommitViewService";
import * as Resources from "Repos/Views/CommitHub/Resources";
import { ITfsNavigationService } from "Tfs/Platform/Navigation";
import { ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IPerformanceScenarioPendingWork, PerformanceScenario, PerformanceScenarioContext } from "VSS/Platform/Components/Performance";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssHistoryService } from "VSS/Platform/History";
import { Components, useComponentContext } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Ago } from "VSSUI/Ago";
import { Card } from "VSSUI/Card";
import { ClipboardButton } from "VSSUI/Clipboard";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";
import { CustomHeader, HeaderTitle, HeaderTitleArea, HeaderTitleRow, TitleSize } from "VSSUI/Header";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Icon } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IVssContributedTab, TabBar, TabContent } from "VSSUI/Tabs";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { VssPersona } from "VSSUI/VssPersona";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";
import { CommitFilesTab } from "./CommitFilesTab";
import { CommitLinksTab } from "./CommitLinksTab";

function getBuiltInCommitTabs(): IVssContributedTab[] {
    return [
        {
            id: "files",
            name: Resources.Files,
            render: () => <CommitFilesTab />
        },
        {
            id: "details",
            name: Resources.Details,
            render: () => <CommitLinksTab />
        }
    ];
}

const CommitHub = (): JSX.Element => {
    const { pageContext } = useComponentContext();
    const commitViewService = pageContext.getService<ICommitViewService>("ICommitViewService");
    const navigationService: IGitNavigationService = getGitNavigationService(pageContext);

    let secondaryText = "";
    let actionHref: string | undefined;

    if (!commitViewService.isCommitValid()) {
        const repo = getGitRepository(pageContext)!;
        const reposRoutingService = pageContext.getService<IReposRoutingService>("IReposRoutingService");
        actionHref = reposRoutingService.getCommitsUrl(repo.name, repo.project.name);

        const historyService = pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const pageState = historyService.getCurrentRouteValues();
        const commitId = pageState["parameters"];
        if (commitId) {
            secondaryText = format(Resources.FailedToFindCommit, commitId);
        }
    }

    const tabs = new ObservableCollection<IVssContributedTab>();
    tabs.push(getBuiltInCommitTabs());

    let currentTabId = commitViewService.getSelectedTabId().value;
    let currentItemPath = navigationService.getSelectedItem().value.path;

    return (
        <PerformanceScenario area="Repos" scenarioName="commit-details" tti={true}>
            <Surface background={SurfaceBackground.neutral}>
                {commitViewService.isCommitValid() ? (
                    <Page
                        className="custom-scrollbar scroll-auto-hide flex-grow"
                        selectedTabId={commitViewService.getSelectedTabId()}
                        tabProviders={tabs}
                    >
                        <ScreenSizeObserver>
                            {(screenProps: { screenSize: ScreenSize }) => {
                                return <CommitHubHeader pageContext={pageContext} screenSize={screenProps.screenSize} />;
                            }}
                        </ScreenSizeObserver>
                        <Observer selectedTabId={commitViewService.getSelectedTabId()}>
                            {({ selectedTabId }: { selectedTabId: string }) => {
                                return (
                                    <>
                                        <TabBar onSelectedTabChanged={tabId => commitViewService.setSelectedTabId(tabId)} />
                                        <div
                                            className={css(
                                                "page-content flex-column flex-noshrink flex-grow",
                                                selectedTabId === "files" && "padding-horizontal-0 padding-bottom-0",
                                                selectedTabId !== "files" && "page-content-top"
                                            )}
                                        >
                                            <CommitHubTabContent selectedTabId={selectedTabId} />
                                        </div>
                                        <PerformanceScenarioContext.Consumer>
                                            {scenarioProps => {
                                                return (
                                                    <Observer selectedItem={navigationService.getSelectedItem()}>
                                                        {(selectionProps: { selectedItem: IChange }) => {
                                                            const newPath = selectionProps.selectedItem.path !== currentItemPath;
                                                            const newTab = selectedTabId !== currentTabId;

                                                            if (newPath || newTab) {
                                                                const newScenario = scenarioProps.resetScenario()!;
                                                                newScenario.addProperties({ inPageNavigate: true });
                                                                newScenario.addProperties({ switchFiles: newPath });
                                                                newScenario.addProperties({ switchTabs: newTab });

                                                                currentItemPath = selectionProps.selectedItem.path;
                                                                currentTabId = selectedTabId;

                                                                scenarioProps.getScenario()!.addProperties({
                                                                    isFolder: selectionProps.selectedItem.isFolder,
                                                                    tab: selectedTabId
                                                                });
                                                            }

                                                            return null;
                                                        }}
                                                    </Observer>
                                                );
                                            }}
                                        </PerformanceScenarioContext.Consumer>
                                    </>
                                );
                            }}
                        </Observer>
                    </Page>
                ) : (
                    <Page className="flex-grow custom-scrollbar scroll-auto-hide">
                        <div className="page-content page-content-top flex-column flex-grow flex-noshrink rhythm-vertical-16">
                            <Card className="flex-grow flex-center no-push-results" contentProps={{ contentPadding: false }}>
                                <ZeroData
                                    className={"flex-grow"}
                                    imagePath={getAssetLocation(pageContext, "ms.vss-code-web/commit-hub-content-lwp/Content/no-results.png")}
                                    imageAltText={"Image"}
                                    primaryText={Resources.CommitNotFound}
                                    secondaryText={secondaryText}
                                    actionType={ZeroDataActionType.ctaButton}
                                    actionText={Resources.GoToCommits}
                                    actionHref={actionHref}
                                />
                            </Card>
                        </div>
                    </Page>
                )}
            </Surface>
        </PerformanceScenario>
    );
};

interface ICommitHubHeaderProps {
    pageContext: IVssPageContext;
    screenSize: ScreenSize;
}

const CommitHubHeader = (props: ICommitHubHeaderProps): JSX.Element => {
    const { pageContext, screenSize } = props;

    const shaCopied = new ObservableValue<boolean>(false);

    const commitViewService = pageContext.getService<ICommitViewService>("ICommitViewService");
    const author = commitViewService.getAuthor();
    const branchStats = commitViewService.getBranchStats();
    const committer = commitViewService.getCommitter();
    const statuses = commitViewService.getStatuses();
    const comment = commitViewService.getCommitMessage();
    const shortComment = commitViewService.getShortCommitMessage();
    const commitId = commitViewService.getCommitId();
    const shortCommitId = getShortCommitId(commitId);

    const repo = commitViewService.getRepository();
    const repoName = repo.name;
    const projectName = repo.project.name;

    const reposRoutingService = getReposRoutingService(pageContext);

    const authorAndCommitterMatch = author && committer && author.name === committer.name;

    const navService = pageContext.getService<ITfsNavigationService>("ITfsNavigationService");
    navService.setPageTitle(format(Resources.DocumentTitleFormat, shortCommitId, shortComment));
    
    // We are splitting the comment into lines with max length of 72 symbols
    const toolTipContent = renderTooltipParagraphs(truncateCommentIntoParagraphs(comment, 72));

    return (
        <CustomHeader>
            <div className="bolt-header-content-area flex-row flex-grow flex-self-stretch margin-bottom-4">
                <HeaderTitleArea className="shadow-padding">
                    <HeaderTitleRow className="repos-commit-title-row">
                        <HeaderTitle titleSize={TitleSize.Large} className="text-ellipsis flex-grow">
                            <FocusWithin>
                                {(focusStatus: IFocusWithinStatus) => (
                                    <div className="flex-row">
                                        <Tooltip overflowOnly={shortComment === comment} renderContent={toolTipContent ? () => toolTipContent : undefined} className="repos-comment-tooltip">
                                            <span className="text-ellipsis" onBlur={focusStatus.onBlur} onFocus={focusStatus.onFocus} tabIndex={0}>
                                                {shortComment}
                                            </span>
                                        </Tooltip>
                                        <Observer branchStats={branchStats}>
                                            {() => {
                                                const commitUrl = reposRoutingService.getCommitUrl(
                                                    repoName,
                                                    projectName,
                                                    commitId,
                                                    branchStats.value ? getRefNameFromBranchName(branchStats.value.name) : undefined
                                                );

                                                return (
                                                    <ClipboardButton
                                                        ariaLabel={Resources.CopyTitle}
                                                        className={css(
                                                            "margin-left-8 repos-commit-title-clipboard-button",
                                                            focusStatus.hasFocus && "visible"
                                                        )}
                                                        getContent={() => (
                                                            <>
                                                                <a style={themeIndependentStyle} href={commitUrl}>
                                                                    {format(Resources.CommitLinkFormat, shortCommitId)}
                                                                </a>
                                                                <span style={{ ...themeIndependentStyle, whiteSpace: "pre" }}>
                                                                    {": "}
                                                                    {comment}
                                                                </span>
                                                            </>
                                                        )}
                                                        key="comment"
                                                        onBlur={focusStatus.onBlur}
                                                        onFocus={focusStatus.onFocus}
                                                        showCopiedTooltip={true}
                                                        subtle={true}
                                                        tooltipProps={{
                                                            text: Resources.CopyTitle
                                                        }}
                                                    />
                                                );
                                            }}
                                        </Observer>
                                    </div>
                                )}
                            </FocusWithin>
                        </HeaderTitle>
                        <Observer items={commitViewService.getHeaderMenuItems(screenSize <= ScreenSize.xsmall)}>
                            {(props: { items: IHeaderCommandBarItem[] }) => {
                                return <HeaderCommandBar items={props.items} />;
                            }}
                        </Observer>
                    </HeaderTitleRow>
                    <div className="flex-row flex-center secondary-text rhythm-horizontal-8 flex-wrap">
                        <div className="flex-row flex-center">
                            <div>{shortCommitId}</div>
                            <Observer copied={shaCopied}>
                                {(props: { copied: boolean }) => (
                                    <ClipboardButton
                                        className="hash-copy-button repos-commit-header-hash"
                                        getContent={() => commitViewService.getCommitId()}
                                        key="clipboard"
                                        onCopy={() => (shaCopied.value = true)}
                                        onMouseLeave={() => (shaCopied.value = false)}
                                        subtle={true}
                                        tooltipProps={{
                                            text: props.copied ? Resources.Copied : Resources.CopyFullSha
                                        }}
                                    />
                                )}
                            </Observer>
                        </div>
                        {authorAndCommitterMatch && (
                            <FormatComponent className="flex-row margin-right-4" elementType="div" format={Resources.CommitSubTitle}>
                                <div className="flex-row margin-right-4">
                                    <VssPersona
                                        className="icon-margin"
                                        displayName={committer.name}
                                        imageUrl={committer.imageUrl}
                                        size={"extra-small-plus"}
                                    />
                                    <span className="font-weight-semibold">{committer.name}</span>
                                </div>
                                <Ago className="margin-left-4" date={committer.date} format={AgoFormat.Compact} />
                            </FormatComponent>
                        )}
                        {!authorAndCommitterMatch && (
                            <FormatComponent className="flex-row margin-right-4" elementType="div" format={Resources.CommitAndAuthorSubTitle}>
                                <div className="flex-row margin-right-4">
                                    <VssPersona
                                        className="icon-margin"
                                        displayName={author.name}
                                        imageUrl={author.imageUrl}
                                        size={"extra-small-plus"}
                                    />
                                    <span className="font-weight-semibold">{author.name}</span>
                                </div>
                                <div className="flex-row margin-horizontal-4">
                                    <VssPersona
                                        className="icon-margin"
                                        displayName={committer.name}
                                        imageUrl={committer.imageUrl}
                                        size={"extra-small-plus"}
                                    />
                                    <span className="font-weight-semibold">{committer.name}</span>
                                </div>
                                <Ago className="margin-left-4" date={committer.date} format={AgoFormat.Compact} />
                            </FormatComponent>
                        )}
                        <Observer branchStats={branchStats}>
                            {(props: { branchStats: BranchStats | undefined }) => {
                                return props.branchStats ? (
                                    <div className="flex-row margin-right-4">
                                        <Icon iconName="OpenSource" className="margin-right-4" />
                                        <FPSLink href={props.branchStats.url} removeUnderline={true}>{props.branchStats.name}</FPSLink>
                                        {props.branchStats.associatedPRStats ? (
                                            <>
                                                <span className="margin-left-4">(</span>
                                                <FPSLink href={props.branchStats.associatedPRStats.url}>
                                                    !{props.branchStats.associatedPRStats.id}
                                                </FPSLink>
                                                <span>)</span>
                                            </>
                                        ) : null}
                                    </div>
                                ) : null;
                            }}
                        </Observer>
                        <Observer statuses={statuses}>
                            {(props: { statuses: GitStatus[] }) => {
                                return props.statuses ? <PipelineStatus className="bolt-table-link" statuses={props.statuses} /> : null;
                            }}
                        </Observer>
                    </div>
                </HeaderTitleArea>
            </div>
        </CustomHeader>
    );
};

const CommitHubTabContent = (props: { selectedTabId: string }): JSX.Element => {
    let pendingWork: IPerformanceScenarioPendingWork | undefined;

    React.useEffect(() => {
        // Register and complete pending work before rendering a tab through
        // its component did mount/update.
        if (pendingWork) {
            pendingWork.complete();
        }
    });

    return (
        <PerformanceScenarioContext.Consumer>
            {scenarioProps => {
                pendingWork = scenarioProps.registerPendingWork("pr-render-tab");
                return (
                    <>
                        <ReposMessages className="flex-noshrink margin-bottom-16" showPullRequestSuggestions={false} />
                        <TabContent />
                    </>
                );
            }}
        </PerformanceScenarioContext.Consumer>
    );
};

function truncateCommentIntoParagraphs(text: string, maxLineLength: number): string[] | undefined {    
    const splittedByNaturalBreaks: string[] = text.split(/\r?\n/);
    const maxLengthMask: RegExp = new RegExp('[^]{1,' + maxLineLength + '}', 'g');

    const result: string[] = [];
    for (const line of splittedByNaturalBreaks) {
        const linesArray = line.match(maxLengthMask);

        if (linesArray != null)
            result.push(linesArray.join("\n"))
    }   

    return result;
}

function renderTooltipParagraphs(paragraphs?: string[]) {
    const result = paragraphs && (
        <div>
            {paragraphs.map((str, index) => (
                <p key={index} className="repos-comment-tooltip-paragraph">{str}</p>
            ))}
        </div>
    );
    return result;
}

Components.add("ms.vss-code-web.commit-hub-new", CommitHub);
